import { useMemo, useState } from 'react'
import {
  RANK, cleanRights, grantableRights, localDayKey, presetRightsForPosition, rankOfPosition, userRank,
  type Right, type PublicUserDTO, type UserListItemDTO,
} from '@wf/shared'
import { ApiError } from '../api/client'
import { useAuth } from '../app/auth'
import { useCreateOnboardCode, useCreateResetCode, useTeam, useUpdateUser } from '../onboarding/useOnboarding'
import { CodeReveal } from '../ui/CodeReveal'
import { PermissionsMatrix } from './PermissionsMatrix'

// Chef-UI „Team": Mitarbeiter per Einmal-Code einladen (Position + Rechte-Matrix mit Positions-Preset) und
// Bestandsnutzer mit NIEDRIGEREM Rang bearbeiten (Rechte-Matrix → PATCH /users/:id). Codes werden GENAU EINMAL
// angezeigt (Backend speichert nur den Hash).

// Schnellanleitung über dem Einladen-Panel: einmal wegklickbar, Entscheidung überlebt Reloads via localStorage
// (Key-Konvention wie wf.timer.v1). Privatmodus/gesperrter Storage → Banner erscheint einfach wieder, nie werfen.
const INTRO_KEY = 'wf.teamintro.v1'

function introDismissed(): boolean {
  try {
    return localStorage.getItem(INTRO_KEY) === 'dismissed'
  } catch {
    return false
  }
}

function IntroBanner() {
  const [dismissed, setDismissed] = useState(introDismissed)
  if (dismissed) return null
  function dismiss() {
    setDismissed(true)
    try {
      localStorage.setItem(INTRO_KEY, 'dismissed')
    } catch {
      // localStorage gesperrt: nur für diese Sitzung ausblenden.
    }
  }
  return (
    <div className="wf-introbanner" role="note">
      <div className="wf-introbanner__body">
        <strong className="wf-introbanner__title">💡 Schnellanleitung: So vergibst du Rechte</strong>
        <ol className="wf-introbanner__steps">
          <li>Wähle eine <b>Position</b> (z. B. Mechaniker) — das System setzt automatisch ein praxiserprobtes Standard-Preset.</li>
          <li>Passe die Stufen <b>Aus / Nur Lesen / Bearbeiten</b> bei Bedarf pro Bereich an.</li>
          <li>Zusatzrechte mit ⚠️ (z. B. „Rechnung stornieren“) sind besonders kritisch — nur an vertrauenswürdige Mitarbeiter vergeben.</li>
        </ol>
      </div>
      <button type="button" className="wf-introbanner__close" onClick={dismiss} aria-label="Schnellanleitung ausblenden">×</button>
    </div>
  )
}

/** Positionen unterhalb des eigenen Rangs (der Server erzwingt es zusätzlich). */
function positionsBelow(me: PublicUserDTO): string[] {
  const myRank = userRank(me)
  return Object.keys(RANK).filter((p) => RANK[p]! < myRank)
}

/** Preset-Rechte einer Position, gefiltert auf das, was der Actor diesem Ziel-Rang geben darf (kein Über-Rang). */
function presetCapsFor(me: PublicUserDTO, position: string): Right[] {
  const g = new Set(grantableRights(me, rankOfPosition(position)))
  return presetRightsForPosition(position).filter((r) => g.has(r))
}

/** Einladungs-Panel: Position (Rang unter dem eigenen) → Preset in der Matrix; Rechte anpassbar → Onboarding-Code. */
function InvitePanel({ me }: { me: PublicUserDTO }) {
  const create = useCreateOnboardCode()
  const positions = positionsBelow(me)
  const initialPos = positions.includes('Mechaniker') ? 'Mechaniker' : (positions[0] ?? '')
  const [position, setPosition] = useState(initialPos)
  const [caps, setCaps] = useState<Right[]>(() => presetCapsFor(me, initialPos))
  const grantable = useMemo(() => new Set(grantableRights(me, rankOfPosition(position))), [me, position])

  // Positionswechsel beim ANLEGEN übernimmt das Preset automatisch (Neuanlage → kein Datenverlust-Risiko).
  function changePosition(p: string) {
    setPosition(p)
    setCaps(presetCapsFor(me, p))
  }

  return (
    <div className="wf-card">
      <h2>Mitarbeiter einladen</h2>
      <IntroBanner />
      <p className="hint">Erzeuge einen Einmal-Code. Der Mitarbeiter wählt damit Benutzername und Passwort selbst. Die Position setzt Standard-Rechte, die du anpassen kannst.</p>
      <label className="wf-field">
        <span className="wf-label">Position</span>
        <select className="wf-input" value={position} onChange={(e) => changePosition(e.target.value)}>
          {positions.map((p) => (
            <option key={p} value={p}>{p}</option>
          ))}
        </select>
      </label>
      <span className="wf-label">Rechte</span>
      <PermissionsMatrix value={caps} onChange={setCaps} grantable={grantable} disabled={create.isPending} />
      {create.isError && (
        <div className="error" role="alert">
          {create.error instanceof ApiError ? create.error.message : 'Code konnte nicht erzeugt werden'}
        </div>
      )}
      <div className="wf-form__actions">
        <button
          type="button"
          className="wf-btn wf-btn--primary"
          onClick={() => position && create.mutate({ position, permissions: caps })}
          disabled={create.isPending || !position}
        >
          {create.isPending ? 'Erzeuge…' : 'Einladungs-Code erzeugen'}
        </button>
      </div>
      {create.data && <CodeReveal code={create.data.code} hint={`Rolle: ${create.data.position}.`} />}
    </div>
  )
}

/** Inline-Editor für einen Bestandsnutzer: Position + Rechte-Matrix → PATCH /users/:id. */
function MemberEditor({ member, me, onClose }: { member: UserListItemDTO; me: PublicUserDTO; onClose: () => void }) {
  const update = useUpdateUser()
  const positions = positionsBelow(me)
  const startPos = member.position && positions.includes(member.position) ? member.position : (positions[0] ?? '')
  const [position, setPosition] = useState(startPos)
  const [caps, setCaps] = useState<Right[]>(() => cleanRights(member.permissions))
  const [birthdate, setBirthdate] = useState(member.birthdate ?? '')
  // Urlaubskonto (2a): Anzeige/Eingabe in TAGEN (halbe erlaubt), gespeichert als Zehntel-Integer.
  const [vacDays, setVacDays] = useState(member.vacationDaysTenths != null ? String(member.vacationDaysTenths / 10) : '')
  const [vacCarry, setVacCarry] = useState(member.vacationCarryTenths ? String(member.vacationCarryTenths / 10) : '')
  const [entryDate, setEntryDate] = useState(member.entryDate ?? '')
  // Schutz-Schalter (Phase 2/3): Beschäftigungsart + optionaler Stundenlohn (€) + MuSchG (Art.-9-Datum!).
  const [beschart, setBeschart] = useState(member.beschaeftigungsart ?? 'sv_pflichtig')
  const [lohn, setLohn] = useState(member.stundenlohnCents != null ? String(member.stundenlohnCents / 100) : '')
  const [muschutz, setMuschutz] = useState(member.mutterschutz === 1 || member.mutterschutz === true)
  const [termin, setTermin] = useState(member.entbindungstermin ?? '')
  const [pnr, setPnr] = useState(member.personalnummer ?? '')
  // Azubi-Block: Ausbildungsverhältnis als DATUMS-PAAR (nach dem entryDate-Muster, NICHT nach der
  // Mutterschutz-Checkbox — die stempelt beim Anhaken automatisch auf heute, und ein Ausbildungsvertrag
  // beginnt fast immer rückwirkend zum 01.08. des Vorjahres).
  const [ausbVon, setAusbVon] = useState(member.ausbildungVon ?? '')
  const [ausbBis, setAusbBis] = useState(member.ausbildungBis ?? '')
  const grantable = useMemo(() => new Set(grantableRights(me, rankOfPosition(position))), [me, position])

  function save() {
    const days = vacDays.trim() === '' ? null : Math.round(Number(vacDays.replace(',', '.')) * 10)
    const carry = vacCarry.trim() === '' ? 0 : Math.round(Number(vacCarry.replace(',', '.')) * 10)
    update.mutate(
      {
        id: member.id,
        body: {
          position,
          permissions: caps,
          birthdate: birthdate || null,
          vacationDaysTenths: days != null && Number.isFinite(days) ? days : null,
          vacationCarryTenths: Number.isFinite(carry) ? carry : 0,
          entryDate: entryDate || null,
          ausbildungVon: ausbVon || null,
          ausbildungBis: ausbBis || null,
          beschaeftigungsart: beschart,
          stundenlohnCents: lohn.trim() === '' ? null : Math.round(Number(lohn.replace(',', '.')) * 100),
          mutterschutz: muschutz,
          entbindungstermin: termin || null,
          personalnummer: pnr.trim() || null,
        },
      },
      { onSuccess: onClose },
    )
  }

  return (
    <div className="wf-membereditor">
      <label className="wf-field">
        <span className="wf-label">Position</span>
        {/* Beim BEARBEITEN wird das Preset NICHT automatisch überschrieben (kein Datenverlust bestehender Rechte)
            — nur der Button unten setzt es bewusst. */}
        <select className="wf-input" value={position} onChange={(e) => setPosition(e.target.value)} disabled={update.isPending}>
          {positions.map((p) => (
            <option key={p} value={p}>{p}</option>
          ))}
        </select>
      </label>
      <label className="wf-field">
        <span className="wf-label">Geburtsdatum (für den Jugendschutz — unter 18 gilt automatisch das JArbSchG)</span>
        <input
          className="wf-input"
          type="date"
          aria-label="Geburtsdatum"
          min="1900-01-01"
          max={localDayKey(Date.now())}
          value={birthdate}
          onChange={(e) => setBirthdate(e.target.value)}
          disabled={update.isPending}
        />
      </label>
      <label className="wf-field">
        <span className="wf-label">Urlaubsanspruch pro Jahr (Tage, halbe erlaubt — leer = kein Urlaubskonto)</span>
        <input className="wf-input" type="number" step="0.5" min="0" max="999" aria-label="Urlaubsanspruch" value={vacDays} onChange={(e) => setVacDays(e.target.value)} disabled={update.isPending} />
      </label>
      <label className="wf-field">
        <span className="wf-label">Übertrag aus dem Vorjahr (Tage)</span>
        <input className="wf-input" type="number" step="0.5" aria-label="Urlaubs-Übertrag" value={vacCarry} onChange={(e) => setVacCarry(e.target.value)} disabled={update.isPending} />
      </label>
      <label className="wf-field">
        <span className="wf-label">Personalnummer im Lohnsystem (für den Lohn-Export; leer = interne Nummer)</span>
        <input className="wf-input" aria-label="Personalnummer" maxLength={20} value={pnr} onChange={(e) => setPnr(e.target.value)} disabled={update.isPending} />
      </label>
      <label className="wf-field">
        <span className="wf-label">Beschäftigungsart (Minijob wird bei Annäherung an die Monatsgrenze überwacht)</span>
        <select className="wf-input" aria-label="Beschäftigungsart" value={beschart} onChange={(e) => setBeschart(e.target.value)} disabled={update.isPending}>
          <option value="sv_pflichtig">Sozialversicherungspflichtig</option>
          <option value="minijob">Minijob (geringfügig)</option>
          <option value="kurzfristig">Kurzfristig beschäftigt</option>
        </select>
      </label>
      <label className="wf-field">
        <span className="wf-label">Stundenlohn in € (optional — präzisiert die Minijob-Grenze)</span>
        <input className="wf-input" type="number" step="0.01" min="0" aria-label="Stundenlohn" value={lohn} onChange={(e) => setLohn(e.target.value)} disabled={update.isPending} />
      </label>
      <label className="wf-field wf-field--check">
        <input type="checkbox" checked={muschutz} onChange={(e) => setMuschutz(e.target.checked)} disabled={update.isPending} /> Mutterschutz aktiv
        (strengere Grenzen: max 8,5 h/Tag, keine Arbeit 20–6 Uhr)
      </label>
      {muschutz ? (
        <label className="wf-field">
          <span className="wf-label">Voraussichtlicher Entbindungstermin (optional)</span>
          <input className="wf-input" type="date" aria-label="Entbindungstermin" value={termin} onChange={(e) => setTermin(e.target.value)} disabled={update.isPending} />
        </label>
      ) : null}
      <label className="wf-field">
        <span className="wf-label">Eintrittsdatum (für die anteilige Berechnung im ersten Jahr)</span>
        <input className="wf-input" type="date" aria-label="Eintrittsdatum" min="1900-01-01" value={entryDate} onChange={(e) => setEntryDate(e.target.value)} disabled={update.isPending} />
      </label>
      <fieldset className="wf-field">
        <legend className="wf-label">Ausbildung (nur bei Auszubildenden)</legend>
        <p className="wf-hint">
          Steuert die Berufsschul-Serien und die Freistellungsregeln nach § 15 BBiG — die gelten auch für volljährige Azubis. Die
          Jugendschutz-Grenzen selbst hängen dagegen am Geburtsdatum, nicht hieran.
        </p>
        <div className="wf-row">
          <label className="wf-field">
            <span className="wf-label">von</span>
            <input
              className="wf-input"
              type="date"
              aria-label="Ausbildung von"
              min="1900-01-01"
              value={ausbVon}
              onChange={(e) => setAusbVon(e.target.value)}
              disabled={update.isPending}
            />
          </label>
          <label className="wf-field">
            <span className="wf-label">bis (leer = läuft noch)</span>
            <input
              className="wf-input"
              type="date"
              aria-label="Ausbildung bis"
              min="1900-01-01"
              value={ausbBis}
              onChange={(e) => setAusbBis(e.target.value)}
              disabled={update.isPending}
            />
          </label>
        </div>
      </fieldset>
      <PermissionsMatrix value={caps} onChange={setCaps} grantable={grantable} disabled={update.isPending} />
      {update.isError && (
        <div className="error" role="alert">
          {update.error instanceof ApiError ? update.error.message : 'Änderung konnte nicht gespeichert werden'}
        </div>
      )}
      <div className="wf-form__actions">
        <button type="button" className="wf-btn wf-btn--sm" onClick={() => setCaps(presetCapsFor(me, position))} disabled={update.isPending}>
          Auf Positions-Standard setzen
        </button>
        <span className="wf-spacer" />
        <button type="button" className="wf-btn wf-btn--sm" onClick={onClose} disabled={update.isPending}>Abbrechen</button>
        <button type="button" className="wf-btn wf-btn--primary wf-btn--sm" onClick={save} disabled={update.isPending}>
          {update.isPending ? 'Speichern…' : 'Speichern'}
        </button>
      </div>
    </div>
  )
}

/** Mitarbeiter-Zeile: Seat-Status (aktiv/inaktiv) + Deaktivieren/Reaktivieren, Reset-Code und Rechte-Editor. */
function MemberRow({ member, me }: { member: UserListItemDTO; me: PublicUserDTO }) {
  const create = useCreateResetCode()
  const setActive = useUpdateUser()
  const [editing, setEditing] = useState(false)
  const [confirming, setConfirming] = useState(false) // Ja/Nein-Panel „sofort deaktivieren?" offen
  const isActive = member.active === 1
  // Hierarchie-Gate (Client-Spiegel von canManageUser): strikt höherer Rang als das Ziel, nicht man selbst.
  // Bewusst UNABHÄNGIG vom Aktiv-Status — damit der Chef inaktive Mitarbeiter reaktivieren kann.
  const canManage = member.id !== me.id && userRank(me) > rankOfPosition(member.position)

  // Tarif-Modell: Deaktivieren wirkt SOFORT (Zugang gesperrt, Sessions gekappt); Reaktivieren jederzeit (Limit-Check).
  function deactivate() {
    setConfirming(false)
    setActive.mutate({ id: member.id, body: { active: false } })
  }
  function reactivate() {
    setActive.mutate({ id: member.id, body: { active: true } })
  }

  return (
    <div className="wf-card wf-adminrow">
      <div className="wf-adminrow__head">
        <div className="wf-adminrow__title">
          <strong>{member.name}</strong>
          {member.username && <span className="wf-muted">@{member.username}</span>}
          {isActive ? (
            <span className="wf-tag">Seat belegt</span>
          ) : (
            <span className="wf-tag wf-tag--off">inaktiv · kein Seat</span>
          )}
        </div>
        <div className="wf-adminrow__meta">{member.position || '—'}</div>
      </div>
      {canManage && (
        <div className="wf-adminrow__actions">
          {isActive ? (
            <>
              <button type="button" className="wf-btn wf-btn--sm" onClick={() => setEditing((v) => !v)} aria-expanded={editing}>
                {editing ? 'Schließen' : 'Bearbeiten'}
              </button>
              <button type="button" className="wf-btn wf-btn--sm" onClick={() => create.mutate({ userId: member.id })} disabled={create.isPending}>
                {create.isPending ? 'Erzeuge…' : 'Reset-Code erzeugen'}
              </button>
              <button type="button" className="wf-btn wf-btn--sm wf-btn--danger" onClick={() => setConfirming((v) => !v)} disabled={setActive.isPending}>
                {setActive.isPending ? 'Wird deaktiviert…' : 'Deaktivieren'}
              </button>
            </>
          ) : (
            <button type="button" className="wf-btn wf-btn--sm wf-btn--primary" onClick={reactivate} disabled={setActive.isPending}>
              {setActive.isPending ? 'Wird aktiviert…' : 'Reaktivieren (belegt einen Seat)'}
            </button>
          )}
        </div>
      )}
      {/* Bestätigung beim Deaktivieren: der Zugang endet SOFORT (Sessions gekappt). Aufträge/Zeitnachweise bleiben (GoBD),
          der Nutzer kann jederzeit reaktiviert werden. */}
      {canManage && isActive && confirming && (
        <div className="wf-membereditor" role="group" aria-label="Deaktivierung bestätigen">
          <p className="hint">
            „{member.name}“ jetzt deaktivieren? Der Zugang wird <strong>sofort</strong> gesperrt und laufende Sitzungen
            beendet. Aufträge und Zeitnachweise bleiben erhalten (GoBD); der Nutzer kann jederzeit reaktiviert werden.
          </p>
          <div className="wf-form__actions">
            <button type="button" className="wf-btn wf-btn--sm" onClick={() => setConfirming(false)} disabled={setActive.isPending}>
              Abbrechen
            </button>
            <button type="button" className="wf-btn wf-btn--sm wf-btn--danger" onClick={deactivate} disabled={setActive.isPending}>
              Sofort deaktivieren
            </button>
          </div>
        </div>
      )}
      {editing && canManage && isActive && <MemberEditor member={member} me={me} onClose={() => setEditing(false)} />}
      {setActive.isError && (
        <div className="error" role="alert">
          {setActive.error instanceof ApiError ? setActive.error.message : 'Änderung des Seat-Status fehlgeschlagen'}
        </div>
      )}
      {create.isError && (
        <div className="error" role="alert">
          {create.error instanceof ApiError ? create.error.message : 'Reset-Code konnte nicht erzeugt werden'}
        </div>
      )}
      {create.data && <CodeReveal code={create.data.code} hint="Passwort-Reset-Code." />}
    </div>
  )
}

export function TeamPage() {
  const { user } = useAuth()
  const team = useTeam()
  if (!user) return null

  // Belegte Seats = aktive Nutzer (Client-Spiegel von seatsUsed; der Server ist maßgeblich für die Abrechnung).
  const activeCount = team.data?.users.filter((u) => u.active === 1).length ?? 0

  return (
    <section className="wf-view">
      <div className="wf-viewhead">
        <div>
          <h2 className="viewtitle">Team</h2>
          <p className="sub">Mitarbeiter einladen, Rechte vergeben und bearbeiten</p>
        </div>
      </div>

      <InvitePanel me={user} />

      <div className="wf-sectionhead">
        <h3>Mitarbeiter</h3>
        <span className="wf-count">{team.data?.users.length ?? 0}</span>
      </div>
      {team.data && (
        <p className="hint">
          {activeCount} {activeCount === 1 ? 'aktiver Nutzer belegt' : 'aktive Nutzer belegen'} je einen Seat.
          Deaktivierte Nutzer kosten nichts, bleiben aber für Aufträge &amp; Nachweise erhalten.
        </p>
      )}
      {team.isLoading && <div className="muted">Mitarbeiter werden geladen…</div>}
      {team.isError && <div className="error" role="alert">Mitarbeiter konnten nicht geladen werden.</div>}
      {team.data && team.data.users.length === 0 && (
        <div className="wf-empty">
          <div className="wf-empty__title">Noch keine Mitarbeiter</div>
          <div className="wf-empty__text">Lade oben den ersten Mitarbeiter per Einmal-Code ein.</div>
        </div>
      )}
      {team.data?.users.map((m) => (
        <MemberRow key={m.id} member={m} me={user} />
      ))}
    </section>
  )
}
